/**
 * Dark Mode Toggle Component
 * Provides a toggle switch for dark/light mode
 */

import React from 'react';
import { Moon, Sun } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * DarkModeToggle component
 * @param {Object} props
 * @param {string} props.size - Size variant ('sm', 'md', 'lg')
 * @param {string} props.variant - Style variant ('button', 'switch', 'icon')
 * @param {boolean} props.showLabel - Whether to show text label
 * @param {string} props.className - Additional CSS classes
 */
const DarkModeToggle = ({ 
  size = 'md', 
  variant = 'button', 
  showLabel = true,
  className = '' 
}) => {
  // Safe theme usage with fallback
  let isDarkMode = false;
  let toggleTheme = () => {};
  
  try {
    const theme = useTheme();
    isDarkMode = theme.isDarkMode;
    toggleTheme = theme.toggleTheme;
  } catch (error) {
    console.warn('ThemeProvider not available in DarkModeToggle');
  }

  const sizes = {
    sm: {
      button: 'px-2 py-1 text-xs',
      icon: 'w-4 h-4',
      switch: 'w-10 h-5'
    },
    md: {
      button: 'px-3 py-2 text-sm',
      icon: 'w-5 h-5',
      switch: 'w-12 h-6'
    },
    lg: {
      button: 'px-4 py-2 text-base',
      icon: 'w-6 h-6',
      switch: 'w-14 h-7'
    }
  };

  if (variant === 'switch') {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        {showLabel && (
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {isDarkMode ? 'Dark' : 'Light'}
          </span>
        )}
        <button
          onClick={toggleTheme}
          className={`
            relative inline-flex ${sizes[size].switch} rounded-full
            transition-colors duration-200 ease-in-out
            ${isDarkMode ? 'bg-blue-600' : 'bg-gray-300'}
            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
            dark:focus:ring-offset-gray-800
          `}
          role="switch"
          aria-checked={isDarkMode}
          aria-label={`Switch to ${isDarkMode ? 'light' : 'dark'} mode`}
        >
          <span
            className={`
              inline-block w-5 h-5 rounded-full bg-white shadow-lg
              transform transition-transform duration-200 ease-in-out
              ${isDarkMode ? 'translate-x-6' : 'translate-x-0'}
              flex items-center justify-center
            `}
          >
            {isDarkMode ? (
              <Moon className="w-3 h-3 text-blue-600" />
            ) : (
              <Sun className="w-3 h-3 text-yellow-500" />
            )}
          </span>
        </button>
      </div>
    );
  }

  if (variant === 'icon') {
    return (
      <button
        onClick={toggleTheme}
        className={`
          p-2 rounded-md transition-colors duration-200
          text-gray-600 hover:text-gray-900 hover:bg-gray-100
          dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
          dark:focus:ring-offset-gray-800
          ${className}
        `}
        title={`Switch to ${isDarkMode ? 'light' : 'dark'} mode`}
      >
        {isDarkMode ? (
          <Sun className={sizes[size].icon} />
        ) : (
          <Moon className={sizes[size].icon} />
        )}
      </button>
    );
  }

  // Default button variant
  return (
    <button
      onClick={toggleTheme}
      className={`
        flex items-center gap-2 rounded-md font-medium
        transition-colors duration-200
        ${sizes[size].button}
        ${isDarkMode 
          ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' 
          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        }
        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
        dark:focus:ring-offset-gray-800
        ${className}
      `}
    >
      {isDarkMode ? (
        <Sun className={sizes[size].icon} />
      ) : (
        <Moon className={sizes[size].icon} />
      )}
      {showLabel && (
        <span>{isDarkMode ? 'Light Mode' : 'Dark Mode'}</span>
      )}
    </button>
  );
};

export default DarkModeToggle;
